import { useState } from 'react';
import { Badge, ConsolePage, Icon, QueryState, ListSkeleton, Segmented } from '@/components';
import { useQuestions } from '@/hooks/queries';
import type { Question } from '@/types/domain';

// Single-course (ACLS) banks. When multi-course lands, derive these from the course.
const PRE_BANK = 'a0000000-0000-4000-8000-000000000001';
const POST_BANK = 'c0000000-0000-4000-8000-000000000003';
const LETTERS = ['ก', 'ข', 'ค', 'ง', 'จ', 'ฉ'];

function isCorrect(q: Question, oi: number): boolean {
  return Array.isArray(q.answer) ? q.answer.includes(oi) : q.answer === oi;
}

function QuestionCard({ q, n }: { q: Question; n: number }) {
  return (
    <div className="card" style={{ padding: 18 }}>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span className="u-mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>Q{n}</span>
        {q.image && <Badge kind="slate">EKG image</Badge>}
      </div>
      <div className="th" style={{ fontSize: 15, fontWeight: 500, lineHeight: 1.45, marginBottom: 12 }}>{q.stem}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {q.options.map((opt, oi) => {
          const ok = isCorrect(q, oi);
          return (
            <div key={oi} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '9px 12px', borderRadius: 9, border: `1.5px solid ${ok ? 'var(--sage)' : 'var(--line)'}`, background: ok ? 'var(--sage-tint)' : 'transparent' }}>
              <span className="u-mono" style={{ fontSize: 12.5, color: ok ? 'var(--sage-deep)' : 'var(--ink-3)', width: 16, flex: 'none' }}>{LETTERS[oi] ?? oi + 1}</span>
              <span className="th" style={{ fontSize: 14, flex: 1, fontWeight: ok ? 500 : 400 }}>{opt}</span>
              {ok && <Icon name="check" size={15} style={{ color: 'var(--sage)', flex: 'none' }} />}
            </div>
          );
        })}
      </div>
      {q.rationale && (
        <div className="th" style={{ marginTop: 12, padding: '10px 12px', background: 'var(--card-2)', borderRadius: 9, fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.5 }}>{q.rationale}</div>
      )}
    </div>
  );
}

/** Read-only instructor view of the pre/post test questions WITH the answer key. */
export function InstructorTests() {
  const [kind, setKind] = useState<'pre' | 'post'>('pre');
  const query = useQuestions(kind === 'pre' ? PRE_BANK : POST_BANK);
  const questions = query.data ?? [];
  return (
    <ConsolePage title="Test questions" th="ข้อสอบ" crumbs="TEACHING" back="/teach" maxWidth={760}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Segmented
            value={kind}
            onChange={setKind}
            options={[{ value: 'pre' as const, label: 'Pre-test' }, { value: 'post' as const, label: 'Post-test' }]}
          />
          <Badge kind="slate">{questions.length} questions</Badge>
        </div>
        <div style={{ display: 'flex', gap: 8, padding: '10px 12px', background: 'var(--sage-tint)', borderRadius: 9, fontSize: 12.5, color: 'var(--sage-deep)' }}>
          <Icon name="shield" size={15} style={{ flex: 'none', marginTop: 1 }} /> Read-only review. Correct answers are highlighted — for instructors only.
        </div>
        <QueryState query={query} skeleton={<ListSkeleton rows={4} />} isEmpty={() => questions.length === 0} emptyIcon="doc" emptyTitle="No questions in this test yet">
          {() => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {questions.map((q, n) => <QuestionCard key={q.id} q={q} n={n + 1} />)}
            </div>
          )}
        </QueryState>
      </div>
    </ConsolePage>
  );
}
